// Options, formatting, and chart specs for the admin Platform health view.

export const WINDOW_OPTIONS = [
  { value: 24, label: 'Last 24 hours' },
  { value: 168, label: 'Last 7 days' },
  { value: 720, label: 'Last 30 days' },
  { value: 2160, label: 'Last 90 days' },
];

export const KIND_OPTIONS = [
  { value: 'request', label: 'Requests' },
  { value: 'worker', label: 'Sub-agent runs' },
  { value: 'turn', label: 'Swarm turns' },
  { value: 'node', label: 'Workflow nodes' },
];

export const GROUP_OPTIONS = [
  { value: 'stack', label: 'Service' },
  { value: 'endpoint', label: 'Endpoint' },
  { value: 'model', label: 'Model' },
];

const SHORTEST_TREND_HOURS = 168;

const CHART_HEIGHT = 220;

const ERROR_COLOR = '#c0392b';

export const labelOf = (options, value) => (
  options.find((option) => option.value === value)?.label ?? value
);

// A day-by-day chart of one day says nothing, so a trend covers a week at least.
export const trendWindowHours = (windowHours) => (
  Math.max(windowHours, SHORTEST_TREND_HOURS)
);

export const analyticsUrl = (baseUrl, params) => (
  `${baseUrl}/api/admin/latency-analytics?${new URLSearchParams(params)}`
);

export const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60000).toFixed(1)} min`;
};

export const formatRate = (rate) => (
  rate === null || rate === undefined ? '—' : `${(rate * 100).toFixed(2)}%`
);

export const formatCount = (count) => count.toLocaleString('en-US');

export const groupLabel = (group, groupBy) => {
  if (group.key === null || group.key === undefined) return 'No model';
  return groupBy === 'endpoint' ? `${group.stack} ${group.key}` : group.key;
};

// Both charts fill their card, and label days without the year, which would
// crowd out most of them.
const chartFrame = (title) => ({
  title: { text: title, anchor: 'start' },
  height: CHART_HEIGHT,
  autosize: { type: 'fit', contains: 'padding' },
});

const dayAxis = {
  field: 'day',
  type: 'ordinal',
  title: null,
  axis: {
    labelAngle: 0,
    labelOverlap: 'greedy',
    labelExpr: 'slice(datum.label, 5)',
  },
};

// Latency spans orders of magnitude, so P50 stays readable beside a long P95
// only on a log scale.  Days come from rollups on every window, so this is
// always an estimate.  The error rate isn't: rollups hold exact counts.
export const latencyTrendSpec = (days) => ({
  ...chartFrame('Latency by day (UTC, estimated)'),
  data: {
    values: days.map((day) => ({
      day: day.key,
      P50: day.p50_ms,
      P95: day.p95_ms,
    })),
  },
  transform: [{ fold: ['P50', 'P95'], as: ['percentile', 'ms'] }],
  mark: { type: 'line', point: true },
  encoding: {
    x: dayAxis,
    y: {
      field: 'ms',
      type: 'quantitative',
      scale: { type: 'log' },
      title: 'Milliseconds (log scale)',
    },
    color: {
      field: 'percentile',
      type: 'nominal',
      legend: { title: null, orient: 'bottom' },
    },
    tooltip: [
      { field: 'day', type: 'ordinal', title: 'Day' },
      { field: 'percentile', type: 'nominal', title: 'Percentile' },
      { field: 'ms', type: 'quantitative', title: 'ms', format: ',.0f' },
    ],
  },
});

export const errorTrendSpec = (days) => ({
  ...chartFrame('Error rate by day (UTC)'),
  data: {
    values: days.map((day) => ({ day: day.key, rate: day.error_rate })),
  },
  mark: { type: 'line', color: ERROR_COLOR, point: { color: ERROR_COLOR } },
  encoding: {
    x: dayAxis,
    y: {
      field: 'rate',
      type: 'quantitative',
      axis: { format: '.1%' },
      title: 'Errors and timeouts',
    },
    tooltip: [
      { field: 'day', type: 'ordinal', title: 'Day' },
      { field: 'rate', type: 'quantitative', title: 'Rate', format: '.2%' },
    ],
  },
});
